"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import React from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { jwtDecode } from "jwt-decode";
import AlertDialog from "./AlertDialog";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
    ...(state.isDisabled && {
      backgroundColor: "#4b5563",
      opacity: 0.5,
      cursor: "not-allowed",
    }),
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "11px",
    padding: "1px 4px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": {
      backgroundColor: "#f97316",
      color: "#fff",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 50,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
    flexWrap: "wrap",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};

// Multi-select filter styles matching BudgetTab
const multiSelectFilterStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "50px",
    zIndex: 100000,
    fontSize: "14px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "300px",
    maxWidth: "300px",
    minWidth: "260px",
    zIndex: 999999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "380px",
    padding: "8px",
    overflowY: "auto",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
    "&:hover": {
      backgroundColor: "#4b5563",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "flex-start",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "6px",
    margin: 0,
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    cursor: "pointer",
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRadius: "4px",
    ":hover": {
      backgroundColor: "#f97316",
      color: "white",
    },
  }),
};

export default function POForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    costCode: "",
    notes: "",
  });

  // Data state
  const [budgets, setBudgets] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [project, setProject] = useState(null);

  // Selection state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedBudgets, setSelectedBudgets] = useState([]);

  // Filter state (matching BudgetTab structure)
  const [filters, setFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    vendor: [],
    laborId: [],
    descriptionSearch: "",
    size: [],
    category: [],
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 100;

  // Debounced search state
  const [searchInput, setSearchInput] = useState("");
  const searchTimeoutRef = useRef(null);

  // Refs for filter dropdowns
  const conditionRef = useRef(null);
  const sectionRef = useRef(null);
  const bidAreaRef = useRef(null);
  const vendorRef = useRef(null);
  const laborIdRef = useRef(null);
  const descriptionRef = useRef(null);
  const sizeRef = useRef(null);
  const categoryRef = useRef(null);
  const budgetRef = useRef(null);

  // Handle click outside to close filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showFilter) return;

      const filterRefs = {
        conditionName: conditionRef,
        sectionNumber: sectionRef,
        bidArea: bidAreaRef,
        vendor: vendorRef,
        laborId: laborIdRef,
        descriptionSearch: descriptionRef,
        size: sizeRef,
        category: categoryRef,
        budget: budgetRef,
      };

      const activeRef = filterRefs[showFilter];

      if (activeRef?.current && !activeRef.current.contains(event.target)) {
        const isInsidePortal =
          event.target.closest(".css-26l3qy-menu") ||
          event.target.closest('[class*="-menu"]') ||
          event.target.closest('[id^="react-select"]');

        if (!isInsidePortal) {
          setShowFilter(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, selectedBudgets]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [projectRes, vendorsRes, costCodesRes, budgetsAndAssembliesRes] =
          await Promise.all([
            axios.get(`${API_BASE}/projects/${id}`, { headers }),
            axios.get(`${API_BASE}/accounts/vendors`, { headers }),
            axios.get(`${API_BASE}/costCodes`, { headers }),
            axios.get(`${API_BASE}/assemblies/budgets2/project/${id}/for-po`, {
              headers,
            }),
          ]);

        setProject(projectRes.data);
        setVendors(vendorsRes.data);
        setCostCodes(costCodesRes.data);
        setBudgets(budgetsAndAssembliesRes.data.budgets || []);
        setAssemblies(budgetsAndAssembliesRes.data.assemblies || []);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Data Load Error",
          message: `Failed to load data: ${
            err.response?.data?.message || err.message
          }`,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Get all material items from assemblies (only unassigned to any PO)
  const materialItems = useMemo(() => {
    const items = [];

    assemblies.forEach((assembly) => {
      if (selectedBudgets.length > 0) {
        const budgetId = assembly.budget?.toString() || assembly.budget;
        if (!selectedBudgets.includes(budgetId)) {
          return;
        }
      }

      assembly.lineItems
        ?.filter((item) => item.category === "M" && !item.linkedPO)
        .forEach((item) => {
          const budget = budgets.find(
            (b) =>
              b._id === assembly.budget?.toString() || b._id === assembly.budget
          );

          items.push({
            ...item,
            assemblyId: assembly._id,
            conditionName: assembly.conditionName,
            assemblyNumber: assembly.assemblyNumber,
            budgetId: assembly.budget,
            budgetTitle: budget?.title || "Unknown Budget",
            isChangeOrder: budget?.isChangeOrder || false,
          });
        });
    });

    return items;
  }, [assemblies, budgets, selectedBudgets]);

  // Get filter options
  // Get filter options
  const getFilterOptions = useCallback(
    (field) => {
      if (!materialItems.length) return [];

      const uniqueValues = new Set();
      materialItems.forEach((item) => {
        let value;
        switch (field) {
          case "conditionName":
            value = item.conditionName;
            break;
          case "sectionNumber":
            value = item.sectionNumber;
            break;
          case "bidArea":
            value = item.bidArea;
            break;
          case "laborId":
            value = item.laborId && item.laborId !== "-" ? item.laborId : null;
            break;
          case "size":
            value = item.size;
            break;
          case "category":
            value = item.category;
            break;
          case "vendor":
            if (item.vendor) {
              value =
                typeof item.vendor === "object" ? item.vendor._id : item.vendor;
            }
            break;
          default:
            value = item[field];
        }
        if (value) uniqueValues.add(value);
      });

      if (field === "vendor") {
        return [
          { value: "unassigned", label: "Unassigned" },
          ...Array.from(uniqueValues).map((id) => ({
            value: id,
            label: vendors.find((v) => v._id === id)?.companyName || "Unknown",
          })),
        ];
      }

      // Special sorting for conditionName (e.g., "1 - Site Work", "2 - Concrete", etc.)
      if (field === "conditionName") {
        const values = Array.from(uniqueValues);

        // Sort numerically by the leading number, then alphabetically for the rest
        values.sort((a, b) => {
          // Extract number prefix (e.g., "1", "10", "2")
          const numA = parseInt(a.match(/^(\d+)/)?.[1] || "999999", 10);
          const numB = parseInt(b.match(/^(\d+)/)?.[1] || "999999", 10);

          if (numA !== numB) return numA - numB;

          // If numbers are the same, sort alphabetically
          return a.localeCompare(b);
        });

        return values.map((v) => ({
          value: v,
          label: v || "N/A",
        }));
      }

      // Default alphabetical sort for other fields
      return Array.from(uniqueValues)
        .sort()
        .map((v) => ({ value: v, label: v || "N/A" }));
    },
    [materialItems, vendors]
  );

  // Budget options for dropdown
  const budgetOptions = useMemo(() => {
    return budgets.map((b) => ({
      value: b._id,
      label: b.isChangeOrder ? `CO: ${b.title}` : b.title,
    }));
  }, [budgets]);

  // Filter items
  const filteredItems = useMemo(() => {
    return materialItems.filter((item) => {
      // Condition filter
      if (
        filters.conditionName.length > 0 &&
        !filters.conditionName.includes(item.conditionName)
      ) {
        return false;
      }
      // Section filter
      if (
        filters.sectionNumber.length > 0 &&
        !filters.sectionNumber.includes(item.sectionNumber)
      ) {
        return false;
      }
      // Bid Area filter
      if (
        filters.bidArea.length > 0 &&
        !filters.bidArea.includes(item.bidArea)
      ) {
        return false;
      }
      // Labor ID filter
      if (
        filters.laborId.length > 0 &&
        !filters.laborId.includes(item.laborId)
      ) {
        return false;
      }
      // Size filter
      if (filters.size.length > 0 && !filters.size.includes(item.size)) {
        return false;
      }
      // Category filter
      if (
        filters.category.length > 0 &&
        !filters.category.includes(item.category)
      ) {
        return false;
      }
      // Description search
      if (filters.descriptionSearch) {
        const desc = (
          item.costCodeDescription ||
          item.itemDescription ||
          ""
        ).toLowerCase();
        if (!desc.includes(filters.descriptionSearch.toLowerCase())) {
          return false;
        }
      }
      // Vendor filter
      if (filters.vendor.length > 0) {
        const itemVendorId =
          item.vendor && typeof item.vendor === "object"
            ? item.vendor._id
            : item.vendor || null;

        const hasUnassigned = filters.vendor.includes("unassigned");
        const selectedVendorIds = filters.vendor.filter(
          (v) => v !== "unassigned"
        );

        const matchesVendor = selectedVendorIds.includes(itemVendorId);
        const matchesUnassigned = hasUnassigned && itemVendorId === null;

        if (!matchesVendor && !matchesUnassigned) {
          return false;
        }
      }
      return true;
    });
  }, [materialItems, filters]);

  // Paginated items
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, currentPage]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  // Group items by condition for display
  const groupedItems = useMemo(() => {
    const groups = {};
    paginatedItems.forEach((item) => {
      const condition = item.conditionName || "Uncategorized";
      if (!groups[condition]) {
        groups[condition] = [];
      }
      groups[condition].push(item);
    });
    return groups;
  }, [paginatedItems]);

  // Handle filter change
  const handleFilterChange = useCallback((filterType, value) => {
    if (filterType === "descriptionSearch") {
      setFilters((prev) => ({ ...prev, descriptionSearch: value }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [filterType]: value || [],
      }));
    }
  }, []);

  // Debounced description search handler
  const handleDescriptionSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, descriptionSearch: value }));
    }, 300);
  }, []);

  // Toggle filter dropdown
  const toggleFilter = (field) => {
    setShowFilter((prev) => (prev === field ? null : field));
  };

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({
      conditionName: [],
      sectionNumber: [],
      bidArea: [],
      vendor: [],
      laborId: [],
      descriptionSearch: "",
      size: [],
      category: [],
    });
    setSearchInput("");
    setSelectedBudgets([]);
    setSelectedItems([]);
    setShowFilter(null);
    setCurrentPage(1);
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.conditionName.length > 0 ||
      filters.sectionNumber.length > 0 ||
      filters.bidArea.length > 0 ||
      filters.vendor.length > 0 ||
      filters.laborId.length > 0 ||
      filters.size.length > 0 ||
      filters.category.length > 0 ||
      filters.descriptionSearch ||
      selectedBudgets.length > 0
    );
  }, [filters, selectedBudgets]);

  // Handle item selection
  const handleItemSelect = useCallback((item) => {
    setSelectedItems((prev) => {
      const exists = prev.some(
        (si) => si.assemblyId === item.assemblyId && si.lineItemId === item._id
      );

      if (exists) {
        return prev.filter(
          (si) =>
            !(si.assemblyId === item.assemblyId && si.lineItemId === item._id)
        );
      } else {
        return [
          ...prev,
          { assemblyId: item.assemblyId, lineItemId: item._id, item },
        ];
      }
    });
  }, []);

  // Handle select all filtered
  const handleSelectAllFiltered = useCallback(() => {
    const allSelected = filteredItems.every((item) =>
      selectedItems.some(
        (si) => si.assemblyId === item.assemblyId && si.lineItemId === item._id
      )
    );

    if (allSelected) {
      setSelectedItems((prev) =>
        prev.filter(
          (si) =>
            !filteredItems.some(
              (item) =>
                si.assemblyId === item.assemblyId && si.lineItemId === item._id
            )
        )
      );
    } else {
      const newItems = filteredItems
        .filter(
          (item) =>
            !selectedItems.some(
              (si) =>
                si.assemblyId === item.assemblyId && si.lineItemId === item._id
            )
        )
        .map((item) => ({
          assemblyId: item.assemblyId,
          lineItemId: item._id,
          item,
        }));

      setSelectedItems((prev) => [...prev, ...newItems]);
    }
  }, [filteredItems, selectedItems]);

  // Check if all filtered items are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredItems.length === 0) return false;
    return filteredItems.every((item) =>
      selectedItems.some(
        (si) => si.assemblyId === item.assemblyId && si.lineItemId === item._id
      )
    );
  }, [filteredItems, selectedItems]);

  // Check if item is selected
  const isItemSelected = useCallback(
    (item) =>
      selectedItems.some(
        (si) => si.assemblyId === item.assemblyId && si.lineItemId === item._id
      ),
    [selectedItems]
  );

  // Calculate summary
  const summary = useMemo(() => {
    return selectedItems.reduce(
      (acc, si) => {
        const amount =
          si.item.amount || si.item.materialTotal || si.item.itemTotal || 0;
        return {
          itemCount: acc.itemCount + 1,
          totalAmount: acc.totalAmount + amount,
        };
      },
      { itemCount: 0, totalAmount: 0 }
    );
  }, [selectedItems]);

  // Calculate filtered totals
  const filteredTotals = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => ({
        total:
          acc.total +
          (item.amount || item.materialTotal || item.itemTotal || 0),
        materials:
          acc.materials +
          (item.amount || item.materialTotal || item.itemTotal || 0),
      }),
      { total: 0, materials: 0 }
    );
  }, [filteredItems]);

  // Format currency
  const formatCurrency = useCallback((num) => {
    return Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  // Get user ID from token
  const getUserIdFromToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.id;
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  // Get selected budget for PO creation
  const getSelectedBudgetForPO = useCallback(() => {
    if (selectedItems.length > 0) {
      return selectedItems[0].item.budgetId;
    }
    if (selectedBudgets.length > 0) {
      return selectedBudgets[0];
    }
    const primaryBudget = budgets.find((b) => !b.isChangeOrder);
    return primaryBudget?._id;
  }, [selectedItems, selectedBudgets, budgets]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.title.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Title is required.",
      });
      setIsSubmitting(false);
      return;
    }

    if (!formData.costCode) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Cost Code is required.",
      });
      setIsSubmitting(false);
      return;
    }

    if (selectedItems.length === 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Please select at least one item.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const userId = getUserIdFromToken();

      if (!userId) {
        throw new Error("User ID not found. Please log in again.");
      }

      const budgetId = getSelectedBudgetForPO();

      const poData = {
        title: formData.title,
        project: id,
        budget: budgetId,
        costCode: formData.costCode,
        createdBy: userId,
        primaryVendor: formData.primaryVendor || null,
        notes: formData.notes,
        items: selectedItems.map((si) => ({
          assemblyId: si.assemblyId,
          lineItemId: si.lineItemId,
        })),
      };

      await axios.post(`${API_BASE}/purchaseOrders2`, poData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message:
          "Purchase Order created successfully. Items have been grouped under this PO.",
        onConfirm: () => {
          setAlert((prev) => ({ ...prev, open: false }));

          // ── UPDATED NAVIGATION ──
          navigate(`/projects/${id}`, {
            state: {
              initialTab: "Finances", // Parent tab
              subTab: "Purchase Orders", // Exact sub-tab name as used in ProjectView
              fromPOCreation: true, // Optional flag you can use later if needed
              timestamp: Date.now(), // Helps force re-render in some edge cases
            },
            replace: true, // Prevents adding form page to history
          });
        },
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Submission Error",
        message: `Failed to create PO: ${
          err.response?.data?.message || err.message
        }`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Options
  const costCodeOptions = costCodes.map((cc) => ({
    value: cc._id,
    label: cc.code || "Unnamed Cost Code",
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-200">Loading...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6">
      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        open={alert.open}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
        onConfirm={() => {
          setAlert((prev) => ({ ...prev, open: false }));
          if (alert.type === "success") {
            navigate(`/projects/${id}`, {
              state: {
                initialTab: "Finances",
                subTab: "Purchase Orders",
                fromPOCreation: true,
              },
              replace: true,
            });
          }
        }}
        confirmText="OK"
        cancelText="Close"
      />

      {/* Header */}
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">
              Create Purchase Order
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Group material items under a PO. Quantities are updated when
              creating Material Orders.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/projects/${id}`)}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* PO Details */}
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">PO Details</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="PO Title"
                className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Cost Code *
              </label>
              <Select
                options={costCodeOptions}
                value={
                  costCodeOptions.find(
                    (opt) => opt.value === formData.costCode
                  ) || null
                }
                onChange={(opt) =>
                  setFormData((prev) => ({
                    ...prev,
                    costCode: opt?.value || "",
                  }))
                }
                placeholder="Select Cost Code..."
                isClearable
                className="mt-1"
                styles={customStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Notes
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional notes..."
                className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Material Items Section */}
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">
            Select Material Items
          </h2>

          {/* Compact Control Bar - Matching BudgetTab */}
          <div className="bg-gray-800 rounded-lg p-3 mb-3">
            {/* Top Row: Stats and Actions */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4 text-xl">
                <span className="text-gray-400">
                  <span className="text-white font-semibold">
                    {filteredItems.length.toLocaleString()}
                  </span>
                  {filteredItems.length !== materialItems.length && (
                    <span className="text-gray-500">
                      {" "}
                      / {materialItems.length.toLocaleString()}
                    </span>
                  )}{" "}
                  items
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-green-400 font-medium">
                  ${formatCurrency(filteredTotals.total)}
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-blue-400">
                  M: ${formatCurrency(filteredTotals.materials)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllFiltered}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-600 transition-colors"
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
                <span className="text-gray-400 text-sm">
                  {selectedItems.length} selected
                </span>
              </div>
            </div>

            {/* Filter Badges Row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Quick Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleDescriptionSearchChange}
                  placeholder="Search..."
                  className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      handleFilterChange("descriptionSearch", "");
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <span className="text-gray-600">|</span>

              {/* Budget Filter Badge */}
              {selectedBudgets.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                  Budget:{" "}
                  {selectedBudgets.length === 1
                    ? budgetOptions
                        .find((o) => o.value === selectedBudgets[0])
                        ?.label?.substring(0, 15) || "Selected"
                    : `${selectedBudgets.length} selected`}
                  <button
                    type="button"
                    onClick={() => setSelectedBudgets([])}
                    className="hover:text-purple-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Condition Filter Badge */}
              {filters.conditionName.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                  Assembly:{" "}
                  {filters.conditionName.length === 1
                    ? filters.conditionName[0]?.length > 15
                      ? filters.conditionName[0].substring(0, 15) + "…"
                      : filters.conditionName[0]
                    : `${filters.conditionName.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("conditionName", [])}
                    className="hover:text-teal-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Section Filter Badge */}
              {filters.sectionNumber.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-blue-600/80 text-white rounded">
                  Section:{" "}
                  {filters.sectionNumber.length === 1
                    ? filters.sectionNumber[0]
                    : `${filters.sectionNumber.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("sectionNumber", [])}
                    className="hover:text-blue-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Bid Area Filter Badge */}
              {filters.bidArea.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                  Area:{" "}
                  {filters.bidArea.length === 1
                    ? filters.bidArea[0]
                    : `${filters.bidArea.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("bidArea", [])}
                    className="hover:text-green-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Labor ID Filter Badge */}
              {filters.laborId.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                  Labor ID:{" "}
                  {filters.laborId.length === 1
                    ? filters.laborId[0]
                    : `${filters.laborId.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("laborId", [])}
                    className="hover:text-indigo-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Size Filter Badge */}
              {filters.size.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-pink-600/80 text-white rounded">
                  Size:{" "}
                  {filters.size.length === 1
                    ? filters.size[0]
                    : `${filters.size.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("size", [])}
                    className="hover:text-pink-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Vendor Filter Badge */}
              {filters.vendor.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-yellow-600/80 text-white rounded">
                  Vendor:{" "}
                  {filters.vendor.length === 1
                    ? filters.vendor[0] === "unassigned"
                      ? "Unassigned"
                      : (
                          vendors.find((v) => v._id === filters.vendor[0])
                            ?.companyName || "Unknown"
                        ).substring(0, 12)
                    : `${filters.vendor.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("vendor", [])}
                    className="hover:text-yellow-200"
                  >
                    ×
                  </button>
                </span>
              )}

              {/* Clear All */}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {Object.keys(groupedItems).length === 0 ? (
            <p className="text-gray-400">
              No unassigned material items available.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-700 isolate">
              <table className="w-full text-[13px]">
                <thead className="bg-gray-800/95 sticky top-0 z-10">
                  <tr className="border-b border-gray-700">
                    {/* Select All Checkbox */}
                    <th className="w-8 px-1 py-1.5 text-center text-[13px] font-medium text-gray-400">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={handleSelectAllFiltered}
                        className="h-3 w-3 rounded border-gray-500 text-orange-500"
                        title={
                          allFilteredSelected
                            ? "Deselect all"
                            : "Select all filtered"
                        }
                      />
                    </th>

                    {/* Budget Column Header */}
                    <th
                      className={`w-24 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        selectedBudgets.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("budget")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Budget
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {selectedBudgets.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {selectedBudgets.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "budget" && (
                        <div
                          ref={budgetRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "280px",
                            width: "max-content",
                            maxWidth: "400px",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={budgetOptions}
                              value={budgetOptions.filter((opt) =>
                                selectedBudgets.includes(opt.value)
                              )}
                              onChange={(options) =>
                                setSelectedBudgets(
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select budgets..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    {/* Condition/Assembly Column Header */}
                    <th
                      className={`w-28 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.conditionName.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("conditionName")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Assembly
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.conditionName.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.conditionName.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "conditionName" && (
                        <div
                          ref={conditionRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "280px",
                            width: "max-content",
                            maxWidth: "400px",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("conditionName")}
                              value={getFilterOptions("conditionName").filter(
                                (opt) =>
                                  filters.conditionName.includes(opt.value)
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "conditionName",
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select conditions..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    {/* Section Column Header */}
                    <th
                      className={`w-16 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.sectionNumber.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("sectionNumber")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Section
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.sectionNumber.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.sectionNumber.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "sectionNumber" && (
                        <div
                          ref={sectionRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "180px",
                            width: "max-content",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("sectionNumber")}
                              value={getFilterOptions("sectionNumber").filter(
                                (opt) =>
                                  filters.sectionNumber.includes(opt.value)
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "sectionNumber",
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    {/* Labor ID Column Header */}
                    <th
                      className={`w-16 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.laborId.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("laborId")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Labor ID
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.laborId.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.laborId.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "laborId" && (
                        <div
                          ref={laborIdRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "180px",
                            width: "max-content",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("laborId")}
                              value={getFilterOptions("laborId").filter((opt) =>
                                filters.laborId.includes(opt.value)
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "laborId",
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    {/* Description Column Header */}
                    <th
                      className={`min-w-[180px] px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.descriptionSearch
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("descriptionSearch")}
                      title="Click to search"
                    >
                      <span className="flex items-center gap-0.5">
                        Description
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </span>
                      {showFilter === "descriptionSearch" && (
                        <div
                          ref={descriptionRef}
                          className="absolute z-20 mt-1 bg-gray-700 border border-gray-600 rounded shadow-xl w-56"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-1.5">
                            <input
                              type="text"
                              value={searchInput}
                              onChange={handleDescriptionSearchChange}
                              placeholder="Type to search..."
                              className="w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-[13px] py-1 px-2 focus:outline-none focus:border-orange-500"
                              autoFocus
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    {/* Size Column Header */}
                    <th
                      className={`w-16 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.size.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("size")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Size
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.size.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.size.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "size" && (
                        <div
                          ref={sizeRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "150px",
                            width: "max-content",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("size")}
                              value={getFilterOptions("size").filter((opt) =>
                                filters.size.includes(opt.value)
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "size",
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Req Qty
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Pending
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Ordered
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Cost
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                      Amount
                    </th>

                    {/* Vendor Column Header */}
                    <th
                      className={`w-24 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.vendor.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("vendor")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Vendor
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.vendor.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.vendor.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "vendor" && (
                        <div
                          ref={vendorRef}
                          className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                          style={{
                            minWidth: "225px",
                            width: "max-content",
                            maxWidth: "225px",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="bg-gray-700 border border-gray-500 rounded-lg shadow-2xl overflow-hidden"
                            style={{
                              width: "225px",
                              maxWidth: "225px",
                            }}
                          >
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("vendor")}
                              value={getFilterOptions("vendor").filter((opt) =>
                                filters.vendor.includes(opt.value)
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "vendor",
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select vendors..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    {/* Bid Area Column Header */}
                    <th
                      className={`w-20 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                        filters.bidArea.length > 0
                          ? "text-orange-400"
                          : "text-gray-400"
                      }`}
                      onClick={() => toggleFilter("bidArea")}
                      title="Click to filter"
                    >
                      <span className="flex items-center gap-0.5">
                        Bid Area
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {filters.bidArea.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {filters.bidArea.length}
                          </span>
                        )}
                      </span>
                      {showFilter === "bidArea" && (
                        <div
                          ref={bidAreaRef}
                          className="absolute left-0 top-full mt-1 z-[999999] pointer-events-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="bg-gray-700 border border-gray-500 rounded-lg shadow-2xl overflow-hidden"
                            style={{
                              width: "320px",
                              maxWidth: "320px",
                            }}
                          >
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={getFilterOptions("bidArea")}
                              value={getFilterOptions("bidArea").filter((opt) =>
                                filters.bidArea.includes(opt.value)
                              )}
                              onChange={(options) =>
                                handleFilterChange(
                                  "bidArea",
                                  options ? options.map((o) => o.value) : []
                                )
                              }
                              placeholder="Select..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>

                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {Object.entries(groupedItems).map(
                    ([conditionName, items]) => (
                      <React.Fragment key={conditionName}>
                        {/* Condition Group Header */}
                        <tr className="bg-gray-800/80">
                          <td
                            colSpan="15"
                            className="px-4 py-2 text-sm font-medium text-white"
                          >
                            <span className="flex items-center justify-between">
                              <span>{conditionName}</span>
                              <span className="text-gray-400 text-xs">
                                {items.length} items
                              </span>
                            </span>
                          </td>
                        </tr>

                        {/* Items */}
                        {items.map((item, index) => {
                          const isSelected = isItemSelected(item);
                          const qty = item.requiredQty || item.quantity || 0;
                          const cost = item.cost || item.price || 0;
                          const amount =
                            item.amount ||
                            item.materialTotal ||
                            item.itemTotal ||
                            0;
                          const pending = item.qtyPending || 0;
                          const ordered = item.qtyOrdered || 0;

                          const vendorId =
                            item.vendor && typeof item.vendor === "object"
                              ? item.vendor._id
                              : item.vendor;
                          const vendorName =
                            vendors.find((v) => v._id === vendorId)
                              ?.companyName || "-";

                          return (
                            <tr
                              key={`${item.assemblyId}-${item._id}`}
                              className={`hover:bg-gray-700/50 transition-colors ${
                                index % 2 === 0
                                  ? "bg-gray-800/30"
                                  : "bg-gray-400/5"
                              } ${
                                isSelected
                                  ? "ring-1 ring-orange-500/50 bg-orange-900/20"
                                  : ""
                              }`}
                            >
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleItemSelect(item)}
                                  className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded"
                                />
                              </td>
                              <td
                                className="px-2 py-2 whitespace-nowrap text-sm text-gray-300 truncate max-w-[100px]"
                                title={item.budgetTitle}
                              >
                                {item.budgetTitle?.substring(0, 12) || "-"}
                              </td>
                              <td
                                className="px-2 py-2 text-sm text-gray-300"
                                title={item.conditionName}
                              >
                                <div className="max-w-[120px] truncate">
                                  {item.conditionName || "-"}
                                </div>
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                {item.sectionNumber || "-"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-300">
                                {item.laborId || "-"}
                              </td>
                              <td
                                className="px-2 py-2 text-sm text-gray-200 max-w-[200px] truncate"
                                title={
                                  item.costCodeDescription ||
                                  item.itemDescription
                                }
                              >
                                {item.costCodeDescription ||
                                  item.itemDescription ||
                                  "-"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                {item.size || "-"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white text-right">
                                {qty}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-yellow-400 text-right">
                                {pending || "-"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-green-400 text-right">
                                {ordered || "-"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white text-right">
                                ${formatCurrency(cost)}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white text-right font-medium">
                                ${formatCurrency(amount)}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                {vendorName}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm text-white">
                                {item.bidArea || "-"}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-sm">
                                {item.isChangeOrder ? (
                                  <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded">
                                    CO
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-gray-500/20 text-gray-400 rounded">
                                    Budget
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4 mt-2">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-gray-400 rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                ««
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                ‹ Prev
              </button>

              <div className="flex items-center gap-1 px-4">
                <span className="text-[13px] text-white">Page</span>
                <select
                  value={currentPage}
                  onChange={(e) => setCurrentPage(Number(e.target.value))}
                  className="px-2 py-0.5 text-[13px] bg-gray-700 border border-gray-600 rounded text-gray-200"
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <span className="text-[13px] text-white">of {totalPages}</span>
              </div>

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                Next ›
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600 hover:text-white"
              >
                »»
              </button>
            </div>
          )}

          {/* Summary */}
          {selectedItems.length > 0 && (
            <div className="mt-4 p-4 bg-gray-800 rounded-lg">
              <h3 className="text-md font-semibold text-gray-400 mb-2">
                Selection Summary
              </h3>
              <div className="grid grid-cols-2 gap-4 text-gray-200">
                <div>
                  <span className="text-gray-400">Items Selected:</span>{" "}
                  {summary.itemCount}
                </div>
                <div>
                  <span className="text-gray-400">Estimated Total:</span>{" "}
                  <span className="text-orange-400 font-semibold">
                    ${formatCurrency(summary.totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/projects/${id}`)}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || selectedItems.length === 0}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 ${
              isSubmitting || selectedItems.length === 0
                ? "bg-orange-300 cursor-not-allowed"
                : "bg-orange-400 hover:bg-orange-500"
            }`}
          >
            {isSubmitting ? "Creating..." : "Create Purchase Order"}
          </button>
        </div>
      </form>
    </div>
  );
}
